/**
 * Scene Manager
 * 
 * Core Three.js scene setup with camera, renderer, and render loop.
 * Uses on-demand rendering for optimal performance.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { CameraDefaults, ControlsConfig, VisualizerTheme } from '../types';
import { detectCapabilities, isWebGLSupported } from './WebGLCapabilities';

export interface SceneManagerOptions {
  container: HTMLElement;
  cameraDefaults: CameraDefaults;
  controlsConfig?: ControlsConfig;
  theme?: VisualizerTheme;
  pixelRatio?: number;
}

export class SceneManager {
  public readonly scene: THREE.Scene;
  public readonly camera: THREE.PerspectiveCamera;
  public readonly renderer: THREE.WebGLRenderer;
  public readonly controls: OrbitControls;
  
  private container: HTMLElement;
  private animationFrameId: number | null = null;
  private needsRender = true;
  private isDisposed = false;
  private resizeObserver: ResizeObserver | null = null;
  
  // For animation updates
  private clock = new THREE.Clock();
  private animationCallbacks: Array<(delta: number) => void> = [];
  
  constructor(options: SceneManagerOptions) {
    if (!isWebGLSupported()) {
      throw new Error('WebGL is not supported on this device');
    }
    
    this.container = options.container;
    const capabilities = detectCapabilities();
    
    // Create scene
    this.scene = new THREE.Scene();
    
    // Apply background color from theme
    if (options.theme?.backgroundColor) {
      this.scene.background = new THREE.Color(options.theme.backgroundColor);
    }
    
    // Create camera
    const { width, height } = this.getContainerSize();
    const cam = options.cameraDefaults;
    
    this.camera = new THREE.PerspectiveCamera(
      cam.fov,
      width / height,
      cam.near ?? 0.1,
      cam.far ?? 1000
    );
    this.camera.position.set(...cam.position);
    this.camera.lookAt(...cam.target);
    
    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(options.pixelRatio ?? Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    
    // Enable shadows if WebGL2
    if (capabilities.webgl2) {
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }
    
    this.container.appendChild(this.renderer.domElement);
    
    // Fix cursor and interaction issues
    this.setupCanvasInteraction();
    
    // Create controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.applyControlsConfig(options.cameraDefaults, options.controlsConfig);
    
    // Listen for control changes to trigger render
    this.controls.addEventListener('change', () => this.requestRender());
    
    // Handle resize
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.container);
    
    // Start render loop
    this.startRenderLoop();
  }
  
  /**
   * Apply controls configuration
   */
  private applyControlsConfig(camera: CameraDefaults, config?: ControlsConfig): void {
    // Set orbit target
    this.controls.target.set(...camera.target);
    
    // Distance limits
    this.controls.minDistance = camera.minDistance;
    this.controls.maxDistance = camera.maxDistance;
    
    // Apply optional config
    if (config) {
      this.controls.enableRotate = config.enableRotate ?? true;
      this.controls.enableZoom = config.enableZoom ?? true;
      this.controls.enablePan = config.enablePan ?? true;
      this.controls.autoRotate = config.autoRotate ?? false;
      this.controls.autoRotateSpeed = config.autoRotateSpeed ?? 2;
      this.controls.enableDamping = config.enableDamping ?? true;
      this.controls.dampingFactor = config.dampingFactor ?? 0.05;
      
      if (config.minPolarAngle !== undefined) {
        this.controls.minPolarAngle = config.minPolarAngle;
      }
      if (config.maxPolarAngle !== undefined) {
        this.controls.maxPolarAngle = config.maxPolarAngle;
      }
      if (config.minAzimuthAngle !== undefined) {
        this.controls.minAzimuthAngle = config.minAzimuthAngle;
      }
      if (config.maxAzimuthAngle !== undefined) {
        this.controls.maxAzimuthAngle = config.maxAzimuthAngle;
      }
    } else {
      // Sensible defaults
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.maxPolarAngle = Math.PI * 0.85; // Prevent going under
    }
    
    this.controls.update();
  }
  
  /**
   * Setup canvas interaction to fix cursor and focus issues
   */
  private setupCanvasInteraction(): void {
    const canvas = this.renderer.domElement;
    
    // Ensure canvas can receive pointer events
    canvas.style.touchAction = 'none';
    canvas.style.userSelect = 'none';
    canvas.style.webkitUserSelect = 'none';
    canvas.style.outline = 'none';
    
    // Set cursor styles for different states
    canvas.style.cursor = 'grab';
    
    // Handle cursor changes during interaction
    canvas.addEventListener('mousedown', () => {
      canvas.style.cursor = 'grabbing';
    });
    
    canvas.addEventListener('mouseup', () => {
      canvas.style.cursor = 'grab';
    });
    
    canvas.addEventListener('mouseleave', () => {
      canvas.style.cursor = 'grab';
    });
    
    // Prevent context menu on right-click (allows right-drag for pan)
    canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    
    // Prevent text selection during drag
    canvas.addEventListener('selectstart', (e) => {
      e.preventDefault();
    });
    
    // Ensure focus doesn't get trapped
    canvas.tabIndex = -1;
  }
  
  /**
   * Add an event listener to the canvas element
   */
  public addCanvasEventListener<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    this.renderer.domElement.addEventListener(type, listener, options);
  }
  
  /**
   * Remove an event listener from the canvas element
   */
  public removeCanvasEventListener<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void,
    options?: boolean | EventListenerOptions
  ): void {
    this.renderer.domElement.removeEventListener(type, listener, options);
  }
  
  /**
   * Get container dimensions
   */
  private getContainerSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth || 800,
      height: this.container.clientHeight || 600,
    };
  }
  
  /**
   * Handle container resize
   */
  private handleResize(): void {
    const { width, height } = this.getContainerSize();
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    this.renderer.setSize(width, height);
    this.requestRender();
  }
  
  /**
   * Request a render on the next frame
   */
  public requestRender(): void {
    this.needsRender = true;
  }
  
  /**
   * Start the render loop
   */
  private startRenderLoop(): void {
    const render = () => {
      if (this.isDisposed) return;
      
      this.animationFrameId = requestAnimationFrame(render);
      
      const delta = this.clock.getDelta();
      
      // Update controls (needed for damping and auto-rotate)
      if (this.controls.enableDamping || this.controls.autoRotate) {
        this.controls.update();
        if (this.controls.autoRotate) {
          this.needsRender = true;
        }
      }
      
      // Run animation callbacks
      for (const callback of this.animationCallbacks) {
        callback(delta);
      }
      
      // Only render when needed
      if (this.needsRender) {
        this.renderer.render(this.scene, this.camera);
        this.needsRender = false;
      }
    };
    
    render();
  }
  
  /**
   * Add an animation callback (called every frame)
   */
  public addAnimationCallback(callback: (delta: number) => void): void {
    this.animationCallbacks.push(callback);
  }
  
  /**
   * Remove an animation callback
   */
  public removeAnimationCallback(callback: (delta: number) => void): void {
    const index = this.animationCallbacks.indexOf(callback);
    if (index !== -1) {
      this.animationCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Set environment map for lighting and reflections
   */
  public setEnvironment(envMap: THREE.Texture): void {
    this.scene.environment = envMap;
    this.requestRender();
  }
  
  /**
   * Set background (color, texture, or environment map)
   */
  public setBackground(
    background: THREE.Color | THREE.Texture | null
  ): void {
    this.scene.background = background;
    this.requestRender();
  }
  
  /**
   * Reset camera to defaults
   */
  public resetCamera(defaults: CameraDefaults): void {
    this.camera.position.set(...defaults.position);
    this.controls.target.set(...defaults.target);
    this.camera.fov = defaults.fov;
    this.camera.updateProjectionMatrix();
    this.controls.update();
    this.requestRender();
  }
  
  /**
   * Get current camera state
   */
  public getCameraState(): { position: [number, number, number]; target: [number, number, number] } {
    return {
      position: this.camera.position.toArray() as [number, number, number],
      target: this.controls.target.toArray() as [number, number, number],
    };
  }
  
  /**
   * Capture screenshot as data URL
   */
  public captureScreenshot(width?: number, height?: number): string {
    // Force render
    this.renderer.render(this.scene, this.camera);
    
    if (width && height) {
      // Render at specific size
      const originalSize = this.renderer.getSize(new THREE.Vector2());
      this.renderer.setSize(width, height);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.render(this.scene, this.camera);
      
      const dataUrl = this.renderer.domElement.toDataURL('image/png');
      
      // Restore original size
      this.renderer.setSize(originalSize.x, originalSize.y);
      this.camera.aspect = originalSize.x / originalSize.y;
      this.camera.updateProjectionMatrix();
      
      return dataUrl;
    }
    
    return this.renderer.domElement.toDataURL('image/png');
  }
  
  /**
   * Set auto-rotate
   */
  public setAutoRotate(enabled: boolean, speed?: number): void {
    this.controls.autoRotate = enabled;
    if (speed !== undefined) {
      this.controls.autoRotateSpeed = speed;
    }
  }
  
  /**
   * Set damping
   */
  public setDamping(enabled: boolean, factor?: number): void {
    this.controls.enableDamping = enabled;
    if (factor !== undefined) {
      this.controls.dampingFactor = factor;
    }
  }
  
  /**
   * Set controls enabled state
   */
  public setControlsEnabled(config: { zoom?: boolean; pan?: boolean; rotate?: boolean }): void {
    if (config.zoom !== undefined) {
      this.controls.enableZoom = config.zoom;
    }
    if (config.pan !== undefined) {
      this.controls.enablePan = config.pan;
    }
    if (config.rotate !== undefined) {
      this.controls.enableRotate = config.rotate;
    }
  }
  
  /**
   * Get controls for direct access (advanced)
   */
  public getControls() {
    return this.controls;
  }
  
  /**
   * Get camera for direct access (advanced)
   */
  public getCamera() {
    return this.camera;
  }
  
  /**
   * Clean up all resources
   */
  public dispose(): void {
    this.isDisposed = true;
    
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    this.controls.dispose();
    this.renderer.dispose();
    
    // Remove canvas from DOM
    if (this.renderer.domElement.parentElement) {
      this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
    }
    
    // Clear scene
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry?.dispose();
        if (Array.isArray(object.material)) {
          object.material.forEach((m) => m.dispose());
        } else {
          object.material?.dispose();
        }
      }
    });
    this.scene.clear();
    
    this.animationCallbacks = [];
  }
}
